import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

/** All SAP RequestStatus codes used by Appeal for Contribution. */
export const APPEAL_STATUS_OPTIONS = [
  { key: "", labelKey: "all", label: "All" },
  { key: "N", labelKey: "new", label: "New" },
  { key: "T", labelKey: "to_be_approved", label: "To Be Approved" },
  { key: "P", labelKey: "pending", label: "Pending" },
  { key: "I", labelKey: "in_process", label: "In Process" },
  { key: "A", labelKey: "approved", label: "Approved" },
  { key: "R", labelKey: "rejected", label: "Rejected" },
  { key: "S", labelKey: "saved", label: "Saved" },
  { key: "B", labelKey: "to_be_processed", label: "To Be Processed" },
  { key: "D", labelKey: "processing_complete", label: "Processing Complete" },
  { key: "C", labelKey: "closed", label: "Closed" },
  { key: "K", labelKey: "cancelled", label: "Cancelled" },
  { key: "X", labelKey: "deleted", label: "Deleted" },
];

const STATUS_MAP: Record<string, string> = {
  N: "new",
  T: "to_be_approved",
  P: "pending",
  I: "in_process",
  A: "approved",
  R: "rejected",
  S: "saved",
  B: "to_be_processed",
  D: "processing_complete",
  C: "closed",
  K: "cancelled",
  X: "deleted",
};

export function getLoggedInEmpId(): string {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(
      user?.empId ||
        user?.details?.empId ||
        user?.usrid ||
        user?.emp_id ||
        ""
    ).trim();
  } catch {
    return "";
  }
}

export function getSyncEmpId(empId = getLoggedInEmpId()): string {
  const n = parseInt(String(empId).replace(/\D/g, ""), 10);
  return Number.isNaN(n) ? String(empId || "") : String(n);
}

export function formatSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "-";
  const str = String(value);
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  const date = msMatch
    ? new Date(parseInt(msMatch[1], 10))
    : new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function formatCurrency(val: any): string {
  const num = parseFloat(String(val ?? "").replace(/,/g, ""));
  if (isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function resolveStatusKey(raw: any): string {
  const code = String(raw ?? "").trim();
  return (
    STATUS_MAP[code] ||
    STATUS_MAP[code.toUpperCase()] ||
    (code.toLowerCase() === "save" ? "saved" : "") ||
    code ||
    ""
  );
}

export function getStatusLabel(raw: any, t: (key: string) => string): string {
  const code = String(raw ?? "").trim().toUpperCase();
  const found = APPEAL_STATUS_OPTIONS.find((opt) => opt.key === code);
  if (found) {
    const translated = t(found.labelKey);
    return translated === found.labelKey ? found.label : translated;
  }
  const mapped = resolveStatusKey(raw);
  if (!mapped) return t("pending");
  const translated = t(mapped);
  return translated === mapped ? mapped : translated;
}

export function getStatusBadgeClass(raw: any): string {
  const code = String(raw ?? "").trim().toUpperCase();
  if (code === "A") return "badge bg-success";
  if (code === "R" || code === "K") return "badge bg-danger";
  if (code === "T" || code === "P") return "badge bg-warning text-dark";
  if (code === "I" || code === "B") return "badge bg-info";
  if (code === "N" || code === "D") return "badge bg-primary";
  if (code === "S") return "badge bg-success";
  if (code === "C") return "badge bg-dark";
  if (code === "X") return "badge bg-secondary";

  const s = resolveStatusKey(raw).toLowerCase();
  if (s === "approved" || s === "saved" || s === "settled") return "badge bg-success";
  if (s === "rejected" || s === "cancelled") return "badge bg-danger";
  if (s === "to_be_approved" || s === "pending" || s === "partially_approved") {
    return "badge bg-warning text-dark";
  }
  if (s === "in_process" || s === "to_be_processed") return "badge bg-info";
  if (s === "new" || s === "processing_complete") return "badge bg-primary";
  if (s === "closed") return "badge bg-dark";
  return "badge bg-secondary";
}

export function pickField(obj: any, ...keys: string[]): any {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
      return obj[k];
    }
  }
  return undefined;
}

function unwrap(res: any): any {
  return res?.data ?? res;
}

function extractResults(data: any): any[] {
  const root = unwrap(data);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export interface AppealListResult {
  items: any[];
  total: number;
  page: number;
  lastPage: number;
}

export async function findAllAppealContributions(payload: {
  page?: number;
  limit?: number;
  status?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
}): Promise<AppealListResult> {
  const res: any = await Api.post(API_URLS.APPEAL_CONTRIBUTION.FIND_ALL, {
    page: payload.page ?? 1,
    limit: payload.limit ?? 10,
    status: payload.status ?? "",
    fromDate: payload.fromDate || "2020-01-01",
    toDate: payload.toDate || "2026-12-31",
    search: payload.search ?? "",
  });
  const root = unwrap(res);
  const inner = root?.data ?? root;
  const items = extractResults(res);
  const meta = inner?.meta ?? inner?.d ?? {};
  return {
    items,
    total: Number(meta.total ?? items.length) || items.length,
    page: Number(meta.page ?? payload.page ?? 1) || 1,
    lastPage: Number(meta.last_page ?? meta.lastPage ?? 1) || 1,
  };
}

export async function findOneAppealContribution(referenceNumber: string): Promise<any> {
  const res: any = await Api.post(API_URLS.APPEAL_CONTRIBUTION.FIND_ONE, {
    referenceNumber: String(referenceNumber).trim(),
  });
  const root = unwrap(res);
  return root?.data ?? root;
}

export async function fetchAppealTypes(): Promise<{ label: string; value: string }[]> {
  const res: any = await Api.post(API_URLS.APPEAL_CONTRIBUTION.APPEAL_TYPES, {});
  return extractResults(res).map((item: any) => ({
    label: String(
      item?.Ddtext ?? item?.ddtext ?? item?.text ?? item?.label ?? item ?? ""
    ),
    value: String(item?.Value ?? item?.value ?? ""),
  }));
}

export async function fetchWillingnessOptions(): Promise<string[]> {
  const res: any = await Api.post(
    API_URLS.APPEAL_CONTRIBUTION.WILLINGNESS_OPTIONS,
    {}
  );
  return extractResults(res).map((item: any) => {
    if (typeof item === "string") return item;
    return String(
      item?.Ddtext ??
        item?.ddtext ??
        item?.label ??
        item?.text ??
        item?.WillingnessOption ??
        item?.willingnessOption ??
        item?.value ??
        item
    );
  });
}

export async function fetchDeceasedList(): Promise<any[]> {
  const res: any = await Api.post(API_URLS.APPEAL_CONTRIBUTION.DECEASED_LIST, {});
  return extractResults(res);
}

export async function createAppealContribution(payload: any): Promise<any> {
  return Api.post(API_URLS.APPEAL_CONTRIBUTION.CREATE, payload);
}

export async function syncAppealContribution(empId?: string): Promise<any> {
  return Api.post(API_URLS.APPEAL_CONTRIBUTION.SYNC, {
    empId: getSyncEmpId(empId),
  });
}

export function getRefNo(item: any): string {
  return String(
    pickField(item, "ReferenceNumber", "referenceNumber", "id") ?? "-"
  );
}

export const getReferenceNumber = getRefNo;

export function getDeceasedResults(detail: any): any[] {
  const nested =
    detail?.ToDeceased?.results ??
    detail?.toDeceased?.results ??
    detail?.ToDeceased ??
    detail?.DeceasedList ??
    [];
  return Array.isArray(nested) ? nested : [];
}
